import {Component, computed, input, output} from '@angular/core';
import {DatePipe} from '@angular/common';
import {FiscalJobDetail, FiscalJobItem, FiscalJobSummary} from '../_models/fiscal-jobs/fiscal-job.models';
import {
  archiveFormatLabel,
  coversEveryRegister,
  exportErrorMessage,
  exportItemReason,
  exportItemStateLabel,
  exportItemTone,
  exportRunStateLabel,
  ExportTone,
  formatExportPeriod,
  isDownloadable,
  parseExportPeriod
} from '../_models/exports/data-export.models';

/**
 * One DSFinV-K data export on screen (#770): what period it covers, when it was requested, how far it got, and
 * — once it is finished — the button that hands over the file.
 *
 * ### Why a card per run and an entry per register
 *
 * A run over three registers produces three files, because the export is narrowed to one register at a time.
 * The engine gives each of them its own state, its own error and its own retry, and this card shows them that
 * way rather than averaging them into one line: "two of three ready" is not a state anybody can act on, while
 * "Kasse 2: abgelaufen — erneut versuchen" is.
 *
 * ### The four unsuccessful states keep two tones
 *
 * `expired` and `withdrawn` are instructions ("fordern Sie ihn neu an"); `failed` and `cancelled` are faults.
 * Drawing all four in one red would tell three of those four people the same, mostly wrong, thing — the
 * reasoning the engine's own state model records (#768 ADR §3), carried through to the pixels.
 *
 * ### The period comes off the run, not off its entries
 *
 * A card is rendered for every run in the list and only the opened one has entries loaded, so the period has
 * to ride the summary — which it does. Reading it off the detail model made every collapsed row say „Zeitraum
 * unbekannt", which is the one thing a list of exports may not do (#770, live screen).
 *
 * ### Nothing here names who performs the work
 *
 * Feature #241's whole point. The run is described by what it is and what period it covers; every sentence
 * this card can show comes from {@link exportErrorMessage}, which is reviewed German, and the server strips
 * provider identity from stored text before it ever arrives.
 *
 * ### The retry rule is the SERVER'S, and there is no copy of it here
 *
 * `item.retryable` is rendered as it arrives. This component used to re-derive it as
 * `item.retryable && item.state !== 'completed'`, which papered over a server projection that called a
 * completed entry retryable — two places that could disagree, and the one that actually decides is the
 * server's guard. The rule now lives once, in `FiscalJobStateMachine.IsRetryable`, and both the read model
 * and the guard ask it (#770 review D3).
 *
 * ### Presentational, deliberately
 *
 * It holds no store, makes no request and decides nothing. That is what lets the page own "which run is open"
 * and "which download is in flight" in one place, and it is what makes this component renderable in Storybook
 * in every state including the ones that need a broken far end.
 */
@Component({
  selector: 'legal-data-export-run-card',
  standalone: true,
  imports: [DatePipe],
  templateUrl: './data-export-run-card.component.html'
})
export class DataExportRunCardComponent {
  /** The run, as the list read it. */
  readonly run = input.required<FiscalJobSummary>();

  /**
   * The run with its entries, when this is the opened one — otherwise `null`.
   *
   * Separate from {@link run} because the list carries summaries and only the opened run is read in full. A
   * card that inferred its entries from the summary's counts would be showing numbers rather than outcomes.
   */
  readonly detail = input<FiscalJobDetail | null>(null);

  /** Whether the entries are shown. */
  readonly expanded = input(false);

  /** The entry whose file is being collected right now, or `null`. */
  readonly downloadingItemId = input<string | null>(null);

  /** The entry being re-run right now, or `null`. */
  readonly retryingItemId = input<string | null>(null);

  /** The operator asked to open or close this run. Carries the run's id. */
  readonly toggled = output<string>();

  /** The operator asked for an entry's file. */
  readonly download = output<FiscalJobItem>();

  /** The operator asked for one entry to be produced again. */
  readonly retry = output<FiscalJobItem>();

  /**
   * The period this run covers, or null when its parameters could not be read.
   *
   * Read off {@link run} — the SUMMARY — never off {@link detail}. A card is rendered collapsed for every run
   * in the list and its entries are loaded only when it is opened, so taking the period from the detail model
   * meant every row said „Zeitraum unbekannt" until it was expanded (#770, live screen). One field, one parse, one
   * source: the opened run's own `job.parameters` is the very same value.
   */
  protected readonly period = computed(() => parseExportPeriod(this.run().parameters));

  /** The period as words. Falls back to „Zeitraum unbekannt" rather than to an empty line. */
  protected readonly periodLabel = computed(() => formatExportPeriod(this.period()));

  /**
   * The container THIS run asked for, as words — „ZIP" or „TAR" — or `null` when the period could not be
   * read either (#823).
   *
   * Read off the same {@link period} the period label already reads, so the two can never disagree about
   * which run's parameters they describe.
   */
  protected readonly containerLabel = computed(() => {
    const period = this.period();
    return period ? archiveFormatLabel(period.format) : null;
  });

  /** The run's state as words. */
  protected readonly stateLabel = computed(() => exportRunStateLabel(this.run().state));

  /** The entries of the opened run, or an empty list. */
  protected readonly items = computed<FiscalJobItem[]>(() => this.detail()?.items ?? []);

  /** The panel's id, so the header button can point `aria-controls` at it. */
  protected readonly panelId = computed(() => `export-run-${this.run().id}`);

  /**
   * The run's own sentence, when the run **as a whole** could not be started.
   *
   * Keyed on the run's own `errorCode`, not on `failed` alone (Bug #789). `failed` now also covers the run
   * whose entries every one of them failed, and such a run carries no run-level code because its reasons
   * belong to its entries. Reading `failed` as "there is a run-level sentence" would put a generic red banner
   * above a list of entries that each say something specific and actionable — the same error twice, with the
   * vaguer copy on top.
   */
  protected readonly runError = computed(() => {
    const code = this.run().errorCode;
    return this.run().state === 'failed' && code ? exportErrorMessage(code) : null;
  });

  /** Whether the run is still being produced — what the "läuft" hint and the spinner ride on. */
  protected readonly running = computed(() =>
    this.run().state === 'queued' || this.run().state === 'running');

  /** What an entry covers: one register's own name, or every register of the location. */
  protected label(item: FiscalJobItem): string {
    return coversEveryRegister(item.reference) ? 'Alle Kassen' : item.label ?? 'Kasse';
  }

  /** An entry's state as words. */
  protected itemLabel(item: FiscalJobItem): string {
    return exportItemStateLabel(item.state);
  }

  /** The tone an entry's chip is drawn in. */
  protected tone(item: FiscalJobItem): ExportTone {
    return exportItemTone(item.state);
  }

  /**
   * Why an entry is not finished, in German.
   *
   * The precedence lives in {@link exportItemReason} rather than here, so this screen and the TSE export
   * screen cannot answer the same question differently — which is how both came to render the server's
   * English `ErrorCatalog` description (Bug #789). That string no longer arrives; see
   * {@link fiscalJobItemReason} for the order and why the stored detail is still first.
   */
  protected reason(item: FiscalJobItem): string | null {
    if (item.state === 'completed' || item.state === 'pending' || item.state === 'working') return null;
    return exportItemReason(item);
  }

  /** Whether an entry has a file to collect. */
  protected downloadable(item: FiscalJobItem): boolean {
    return isDownloadable(item);
  }

}
